<template>
  <div class="ide-view">
    <div class="d-flex align-center mb-4">
      <v-btn
        icon
        variant="text"
        class="mr-2"
        @click="voltarSeguro"
      >
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <h1 class="text-h4">{{ t('dev.queenIde') }}</h1>
      
      <v-spacer></v-spacer>
      
      <v-btn-group class="mr-3">
        <v-btn
          variant="text"
          size="small"
          prepend-icon="mdi-content-save"
          @click="salvarArquivo"
        >
          Salvar
        </v-btn>
        <v-btn
          variant="text"
          size="small"
          prepend-icon="mdi-content-save-all"
          @click="salvarTodos"
        >
          Salvar Todos
        </v-btn>
      </v-btn-group>
      
      <v-btn
        color="primary"
        prepend-icon="mdi-rocket-launch"
        :loading="compilando"
        :disabled="!codigoModificado"
        @click="compilarContrato"
      >
        Compilar
      </v-btn>
      
      <v-btn
        color="success"
        prepend-icon="mdi-cloud-upload"
        class="ml-2"
        :disabled="!codigoCompilado"
        @click="implantarContrato"
      >
        Implantar
      </v-btn>
    </div>
    
    <v-card class="mb-3">
      <v-card-text>
        <div class="text-body-1 mb-2">
          Desenvolva, compile e implante contratos inteligentes na linguagem Queen diretamente do seu navegador.
        </div>
        <v-alert
          color="info"
          icon="mdi-information-outline"
          variant="tonal"
          density="compact"
        >
          Queen é a linguagem de contrato inteligente da QuarnX, otimizada para segurança e verificação formal.
        </v-alert>
      </v-card-text>
    </v-card>
    
    <v-row>
      <v-col cols="12" md="2">
        <!-- Explorador de Projetos -->
        <TreeProjectExplorer
          :active-file="arquivoAtual"
          @file-selected="selecionarArquivo"
          class="explorer-height"
        />
      </v-col>
      
      <v-col cols="12" md="7">
        <v-row>
          <v-col cols="12">
            <!-- Editor de código com Monaco Editor -->
            <v-card height="500px">
              <v-toolbar density="compact" color="primary">
                <v-toolbar-title class="text-body-2">
                  {{ arquivoAtual ? arquivoAtual.name : 'Sem arquivo aberto' }}
                </v-toolbar-title>
                
                <v-spacer></v-spacer>
                
                <v-btn-group>
                  <v-btn
                    icon
                    variant="text"
                    size="small"
                    @click="solicitarAjuda"
                    v-tooltip="'Assistente de Código'"
                  >
                    <v-icon>mdi-chat-question</v-icon>
                  </v-btn>
                  
                  <v-btn
                    icon
                    variant="text"
                    size="small"
                    @click="formatarCodigo"
                    v-tooltip="'Formatar Código (Ctrl+Shift+F)'"
                  >
                    <v-icon>mdi-format-align-left</v-icon>
                  </v-btn>
                  
                  <!-- Botão de alternar tema removido temporariamente para evitar travamentos -->
                </v-btn-group>
              </v-toolbar>
              
              <div v-if="arquivoAtual" style="height: calc(100% - 48px);">
                <MonacoEditor
                  v-model:value="editorCodigo"
                  :language="getFileLanguage(arquivoAtual)"
                  theme="vs-dark"
                  @change="onEditorChange"
                  @editor-did-mount="onEditorMounted"
                />
              </div>
              
              <div v-else class="d-flex flex-column align-center justify-center" style="height: calc(100% - 48px);">
                <v-icon size="64" color="grey-lighten-1" class="mb-3">mdi-file-document-outline</v-icon>
                <div class="text-h6 mb-2">{{ t('dev.noFileOpen') }}</div>
                <div class="text-body-2 text-medium-emphasis text-center px-4">
                  Selecione um arquivo no explorador de projetos ou crie um novo arquivo para começar a codificar.
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>
        
        <v-expansion-panels class="mt-3">
          <v-expansion-panel>
            <v-expansion-panel-title>
              <div class="d-flex align-center">
                <v-icon color="error" v-if="errosCompilacao.length > 0" class="mr-2">
                  mdi-alert-circle
                </v-icon>
                <v-icon color="success" v-else-if="codigoCompilado" class="mr-2">
                  mdi-check-circle
                </v-icon>
                <span>{{ t('dev.compileResult') }}</span>
                <v-chip
                  v-if="errosCompilacao.length > 0"
                  size="small"
                  color="error"
                  class="ml-2"
                >
                  {{ errosCompilacao.length }} erros
                </v-chip>
              </div>
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <div v-if="compilando" class="text-center py-3">
                <v-progress-circular
                  indeterminate
                  color="primary"
                ></v-progress-circular>
                <div class="mt-2">{{ t('dev.compiling') }}</div>
              </div>
              
              <div v-else-if="errosCompilacao.length > 0">
                <div
                  v-for="(erro, index) in errosCompilacao"
                  :key="index"
                  class="pa-2 mb-2 rounded bg-error-lighten-4"
                >
                  <div class="d-flex align-center">
                    <v-icon size="small" color="error" class="mr-2">mdi-alert-circle</v-icon>
                    <div>
                      <div class="font-weight-medium">Linha {{ erro.linha }}: {{ erro.mensagem }}</div>
                      <div class="text-caption">{{ erro.contexto }}</div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div v-else-if="codigoCompilado" class="pa-2 rounded bg-success-lighten-4">
                <div class="d-flex align-center">
                  <v-icon size="small" color="success" class="mr-2">mdi-check-circle</v-icon>
                  <div>
                    <div class="font-weight-medium">{{ t('dev.compileSuccess') }}</div>
                    <div class="text-caption">Bytecode gerado: {{ bytecodeGerado.substring(0, 20) }}...</div>
                  </div>
                </div>
              </div>
              
              <div v-else class="text-body-2 text-grey text-center py-2">
                Nenhuma compilação realizada ainda
              </div>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>
      
      <v-col cols="12" md="3">
        <div class="d-flex flex-column h-100">
          <!-- Assistente Queen -->          <v-card class="mb-3 flex-grow-1 d-flex flex-column">
            <v-toolbar density="compact" color="primary">
              <v-toolbar-title class="text-body-2">
                Assistente Queen
              </v-toolbar-title>
            </v-toolbar>
            
            <div class="chat-container flex-grow-1 overflow-y-auto pa-2" ref="chatContainer">
              <div
                v-for="(mensagem, index) in chatMensagens"
                :key="index"
                :class="['chat-message mb-2 pa-2 rounded', 
                         mensagem.tipo === 'assistente' ? 'bg-primary-lighten-5' : 'bg-grey-lighten-3']"
                :style="{ maxWidth: '80%', alignSelf: mensagem.tipo === 'assistente' ? 'flex-start' : 'flex-end' }"
              >
                <div class="text-caption mb-1 font-weight-medium">
                  {{ mensagem.tipo === 'assistente' ? 'Assistente Queen' : 'Você' }}
                </div>
                <div class="text-body-2">{{ mensagem.texto }}</div>
              </div>
            </div>
            
            <div class="chat-input pa-2">
              <v-text-field
                v-model="novaMensagem"
                placeholder="Pergunte algo sobre programação em Queen..."
                density="compact"
                append-inner-icon="mdi-send"
                @click:append-inner="enviarMensagem"
                @keyup.enter="enviarMensagem"
                hide-details
              ></v-text-field>
            </div>
          </v-card>
          
          <!-- Templates e exemplos -->
          <v-card>
            <v-toolbar density="compact" color="primary">
              <v-toolbar-title class="text-body-2">
                Templates e Exemplos
              </v-toolbar-title>
            </v-toolbar>
            
            <v-list density="compact">
              <v-list-item
                v-for="(template, index) in templates"
                :key="index"
                @click="carregarTemplate(template)"
              >
                <template v-slot:prepend>
                  <v-icon :color="template.cor">{{ template.icon }}</v-icon>
                </template>
                <v-list-item-title>{{ template.nome }}</v-list-item-title>
                <v-list-item-subtitle>{{ template.descricao }}</v-list-item-subtitle>
              </v-list-item>
            </v-list>
          </v-card>
          
          <!-- Sandbox de Contratos -->
          <ContractSandbox
            :compiled-code="resultadoCompilacao"
            :is-code-compiled="codigoCompilado"
            @sandbox-reset="resetarCompilacao"
            @contract-deployed="onContratoDeploy"
            class="mt-3"
          />
        </div>
      </v-col>
    </v-row>
    
    <!-- Diálogo de implantação -->
    <v-dialog
      v-model="dialogImplantacao"
      max-width="600"
    >
      <v-card>
        <v-card-title class="text-h5">
          Implantar Contrato
        </v-card-title>
        
        <v-card-text>
          <v-form ref="formImplantacao">
            <v-text-field
              v-model="implantacao.nome"
              label="Nome do Contrato*"
              required
            ></v-text-field>
            
            <v-text-field
              v-model="implantacao.args"
              label="Argumentos do Construtor"
              hint="Separados por vírgula"
            ></v-text-field>
            
            <v-text-field
              v-model="implantacao.valor"
              label="QRX para Enviar"
              type="number"
              suffix="QRX"
              hint="Quantidade de QRX a enviar com a implantação (opcional)"
            ></v-text-field>
            
            <v-select
              v-model="implantacao.gasLimit"
              :items="opcoeGasLimit"
              :label="t('contracts.gasLimit')"
            ></v-select>
          </v-form>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="grey-darken-1"
            variant="text"
            @click="dialogImplantacao = false"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="primary"
            @click="confirmarImplantacao"
            :loading="implantando"
          >
            Implantar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    
    <!-- Snackbar -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
    >
      {{ snackbar.text }}
    </v-snackbar>
  </div>
</template>

<script>
import { useDeveloperStore } from '@/stores/developer'
import { compilarCodigo } from '@/services/compilador'
import { API_BASE_URL } from '@/services/api'
import MonacoEditor from '@/components/developer/MonacoEditor.vue'
import TreeProjectExplorer from '@/components/developer/TreeProjectExplorer.vue'
import ContractSandbox from '@/components/developer/ContractSandbox.vue'

export default {
  name: 'DeveloperIDEView',
  components: {
    MonacoEditor,
    TreeProjectExplorer,
    ContractSandbox
  },
  data() {
    return {
      arquivoAtual: null,
      editorCodigo: '',
      codigoModificado: false,
      codigoCompilado: false,
      compilando: false,
      implantando: false,
      errosCompilacao: [],
      bytecodeGerado: '',
      resultadoCompilacao: null,
      // Tema fixo em vs-dark para evitar travamentos
      editorInstance: null,
      chatMensagens: [
        {
          tipo: 'assistente',
          texto: 'Olá! Sou o assistente Queen. Como posso ajudar com seu desenvolvimento de contratos inteligentes hoje?'
        }
      ],
      novaMensagem: '',
      templates: [
        {
          id: 'contrato',
          nome: 'Contrato Básico',
          descricao: 'Template básico para contrato Queen',
          icon: 'mdi-file-code',
          cor: 'primary',
          codigo: `// Contrato Queen Básico\ncontract MeuContrato {\n    string public nome;\n    address public proprietario;\n    \n    event PropriedadeAlterada(string novoNome);\n    \n    constructor(string memory _nome) {\n        nome = _nome;\n        proprietario = msg.sender;\n    }\n    \n    function alterarNome(string memory _novoNome) public {\n        // Verificação formal\n        verify {\n            requires: msg.sender == proprietario;\n            ensures: keccak256(bytes(nome)) != keccak256(bytes(_novoNome));\n        }\n        \n        nome = _novoNome;\n        emit PropriedadeAlterada(_novoNome);\n    }\n    \n    function obterDetalhes() public view returns (string memory, address) {\n        return (nome, proprietario);\n    }\n}`
        },
        {
          id: 'token',
          nome: 'Token QRC20',
          descricao: 'Template para token padrão QRC20',
          icon: 'mdi-currency-token',
          cor: 'purple',
          codigo: `// Token Queen QRC20\ncontract TokenQRC20 {\n    string public name;\n    string public symbol;\n    uint8 public decimals;\n    uint256 public totalSupply;\n    \n    mapping(address => uint256) public balanceOf;\n    mapping(address => mapping(address => uint256)) public allowance;\n    \n    event Transfer(address indexed from, address indexed to, uint256 value);\n    event Approval(address indexed owner, address indexed spender, uint256 value);\n    \n    constructor(string memory _name, string memory _symbol, uint8 _decimals, uint256 _initialSupply) {\n        name = _name;\n        symbol = _symbol;\n        decimals = _decimals;\n        totalSupply = _initialSupply * 10 ** uint256(_decimals);\n        balanceOf[msg.sender] = totalSupply;\n        emit Transfer(address(0), msg.sender, totalSupply);\n    }\n    \n    function transfer(address _to, uint256 _value) public returns (bool success) {\n        verify {\n            requires: balanceOf[msg.sender] >= _value;\n            ensures: balanceOf[msg.sender] == old(balanceOf[msg.sender]) - _value;\n            ensures: balanceOf[_to] == old(balanceOf[_to]) + _value;\n        }\n        \n        balanceOf[msg.sender] -= _value;\n        balanceOf[_to] += _value;\n        emit Transfer(msg.sender, _to, _value);\n        return true;\n    }\n    \n    function approve(address _spender, uint256 _value) public returns (bool success) {\n        allowance[msg.sender][_spender] = _value;\n        emit Approval(msg.sender, _spender, _value);\n        return true;\n    }\n    \n    function transferFrom(address _from, address _to, uint256 _value) public returns (bool success) {\n        verify {\n            requires: balanceOf[_from] >= _value;\n            requires: allowance[_from][msg.sender] >= _value;\n            ensures: balanceOf[_from] == old(balanceOf[_from]) - _value;\n            ensures: balanceOf[_to] == old(balanceOf[_to]) + _value;\n            ensures: allowance[_from][msg.sender] == old(allowance[_from][msg.sender]) - _value;\n        }\n        \n        balanceOf[_from] -= _value;\n        balanceOf[_to] += _value;\n        allowance[_from][msg.sender] -= _value;\n        emit Transfer(_from, _to, _value);\n        return true;\n    }\n}`
        },
        {
          id: 'nft',
          nome: 'NFT Básico',
          descricao: 'Template para token não fungível (NFT)',
          icon: 'mdi-palette',
          cor: 'blue',
          codigo: `// Queen NFT\ncontract NFTBasico {\n    string public name;\n    string public symbol;\n    \n    struct NFT {\n        uint256 id;\n        string uri;\n        address owner;\n    }\n    \n    mapping(uint256 => NFT) public tokens;\n    mapping(address => uint256[]) public ownerTokens;\n    uint256 public nextTokenId = 1;\n    \n    event Mint(address indexed to, uint256 indexed tokenId, string uri);\n    event Transfer(address indexed from, address indexed to, uint256 indexed tokenId);\n    \n    constructor(string memory _name, string memory _symbol) {\n        name = _name;\n        symbol = _symbol;\n    }\n    \n    function mint(address _to, string memory _uri) public returns (uint256) {\n        uint256 tokenId = nextTokenId++;\n        tokens[tokenId] = NFT(tokenId, _uri, _to);\n        ownerTokens[_to].push(tokenId);\n        \n        emit Mint(_to, tokenId, _uri);\n        return tokenId;\n    }\n    \n    function transfer(address _to, uint256 _tokenId) public {\n        verify {\n            requires: tokens[_tokenId].owner == msg.sender;\n            ensures: tokens[_tokenId].owner == _to;\n        }\n        \n        tokens[_tokenId].owner = _to;\n        \n        // Atualizar arrays de propriedade\n        // Em produção, seria necessário uma lógica mais eficiente para remover do array\n        \n        emit Transfer(msg.sender, _to, _tokenId);\n    }\n    \n    function balanceOf(address _owner) public view returns (uint256) {\n        return ownerTokens[_owner].length;\n    }\n    \n    function tokenURI(uint256 _tokenId) public view returns (string memory) {\n        verify {\n            requires: _tokenId < nextTokenId;\n        }\n        return tokens[_tokenId].uri;\n    }\n}`
        },
        {
          id: 'votacao',
          nome: 'Votação',
          descricao: 'Sistema de votação descentralizado',
          icon: 'mdi-vote',
          cor: 'green',
          codigo: `// Sistema de Votação Queen\ncontract SistemaVotacao {\n    struct Proposta {\n        string descricao;\n        uint256 votosFavoraveis;\n        uint256 votosContrarios;\n        bool finalizada;\n        mapping(address => bool) votou;\n    }\n    \n    address public organizador;\n    mapping(uint256 => Proposta) public propostas;\n    uint256 public numPropostas;\n    \n    event NovaPropostaRegistrada(uint256 indexed propostaId, string descricao);\n    event VotoRegistrado(uint256 indexed propostaId, address eleitor, bool votoFavoravel);\n    event PropostaFinalizada(uint256 indexed propostaId, bool aprovada);\n    \n    constructor() {\n        organizador = msg.sender;\n    }\n    \n    modifier apenasOrganizador() {\n        require(msg.sender == organizador, "Apenas o organizador pode realizar esta ação");\n        _;\n    }\n    \n    function criarProposta(string memory _descricao) public apenasOrganizador returns (uint256) {\n        uint256 propostaId = numPropostas++;\n        Proposta storage p = propostas[propostaId];\n        p.descricao = _descricao;\n        p.finalizada = false;\n        \n        emit NovaPropostaRegistrada(propostaId, _descricao);\n        return propostaId;\n    }\n    \n    function votar(uint256 _propostaId, bool _favoravel) public {\n        Proposta storage p = propostas[_propostaId];\n        \n        verify {\n            requires: !p.finalizada;\n            requires: !p.votou[msg.sender];\n        }\n        \n        p.votou[msg.sender] = true;\n        \n        if (_favoravel) {\n            p.votosFavoraveis += 1;\n        } else {\n            p.votosContrarios += 1;\n        }\n        \n        emit VotoRegistrado(_propostaId, msg.sender, _favoravel);\n    }\n    \n    function finalizarProposta(uint256 _propostaId) public apenasOrganizador {\n        Proposta storage p = propostas[_propostaId];\n        \n        verify {\n            requires: !p.finalizada;\n        }\n        \n        p.finalizada = true;\n        bool aprovada = p.votosFavoraveis > p.votosContrarios;\n        \n        emit PropostaFinalizada(_propostaId, aprovada);\n    }\n    \n    function resultadoProposta(uint256 _propostaId) public view returns (string memory descricao, uint256 votosFavoraveis, uint256 votosContrarios, bool finalizada) {\n        Proposta storage p = propostas[_propostaId];\n        return (p.descricao, p.votosFavoraveis, p.votosContrarios, p.finalizada);\n    }\n}`
        }
      ],
      dialogImplantacao: false,
      implantacao: {
        nome: '',
        args: '',
        valor: 0,
        gasLimit: '1000000'
      },
      opcoeGasLimit: [
        '500000',
        '1000000',
        '2000000',
        '5000000'
      ],
      snackbar: {
        show: false,
        text: '',
        color: 'success',
        timeout: 3000
      }
    }
  },
  computed: {
    developerStore() {
      return useDeveloperStore()
    }
  },
  methods: {
    voltarSeguro() {
      // Verificar se há alterações não salvas
      if (this.codigoModificado) {
        // Confirmar com o usuário
        if (confirm('Existem alterações não salvas. Deseja sair mesmo assim?')) {
          this.$router.push({ name: 'developer' });
        }
      } else {
        this.$router.push({ name: 'developer' });
      }
    },
    
    selecionarArquivo(arquivo) {
      if (!arquivo) {
        this.arquivoAtual = null
        this.editorCodigo = ''
        this.codigoModificado = false
        this.codigoCompilado = false
        return
      }

      // Determinar conteúdo baseado no tipo de arquivo
      let novoCodigo = ''
      if (arquivo.type === 'contract') {
        if (arquivo.name.includes('Token')) {
          novoCodigo = this.templates.find(t => t.id === 'token')?.codigo || ''
        } else if (arquivo.name.includes('NFT')) {
          novoCodigo = this.templates.find(t => t.id === 'nft')?.codigo || ''
        } else {
          novoCodigo = this.templates.find(t => t.id === 'contrato')?.codigo || ''
        }
      } else if (arquivo.type === 'interface') {
        novoCodigo = `// Interface Queen\ninterface IToken {\n  function totalSupply() external view returns (uint256);\n  function balanceOf(address account) external view returns (uint256);\n  function transfer(address to, uint256 amount) external returns (bool);\n}\n`
      } else if (arquivo.type === 'markdown') {
        novoCodigo = `# ${arquivo.name.replace('.md', '')}\n\nDocumentação do projeto.\n`
      } else {
        novoCodigo = `// ${arquivo.name}\n`
      }

      // Atualizar estado sem recriar o editor
      this.arquivoAtual = arquivo
      // Usar nextTick pra garantir que o Vue atualizou o DOM antes de mudar o código
      this.$nextTick(() => {
        this.editorCodigo = novoCodigo
        this.codigoModificado = false
        this.codigoCompilado = false
      })
    },
    
    salvarArquivo() {
      if (!this.arquivoAtual) return
      
      // Simulação de salvamento (em produção, isso salvaria no backend)
      this.mostrarSnackbar(`Arquivo ${this.arquivoAtual.name} salvo com sucesso.`)
      this.codigoModificado = false
    },
    
    salvarTodos() {
      // Simulação de salvamento de todos os arquivos
      this.mostrarSnackbar('Todos os arquivos foram salvos com sucesso.')
      this.codigoModificado = false
    },
    
    formatarCodigo() {
      if (!this.editorInstance) return
      
      // Formatar o código usando o editor Monaco
      this.editorInstance.getAction('editor.action.formatDocument')?.run()
      this.mostrarSnackbar('Código formatado.')
    },
    
    // Função de alternar tema removida para evitar travamentos
    
    onEditorChange(newValue) {
      this.codigoModificado = true
      // Se o código mudou, o estado compilado já não é mais válido
      if (this.codigoCompilado) {
        this.codigoCompilado = false
      }
    },
    
    onEditorMounted(editor) {
      this.editorInstance = editor
    },
    
    getFileLanguage(file) {
      if (!file) return 'queen'
      
      const extension = file.name.split('.').pop()?.toLowerCase()
      
      switch (extension) {
        case 'queen':
          return 'queen'
        case 'md':
          return 'markdown'
        case 'json':
          return 'json'
        default:
          return 'queen'
      }
    },
    
    resetarCompilacao() {
      this.codigoCompilado = false
      this.resultadoCompilacao = null
      this.bytecodeGerado = ''
      this.errosCompilacao = []
    },
    
    onContratoDeploy(info) {
      this.mostrarSnackbar(`Contrato implantado com sucesso no endereço ${info.address}`, 'success')
    },
    
    carregarTemplate(template) {
      if (this.codigoModificado) {
        if (!confirm('Deseja substituir o código atual? Todas as mudanças não salvas serão perdidas.')) {
          return
        }
      }
      
      this.editorCodigo = template.codigo
      this.codigoModificado = true
      this.codigoCompilado = false
      this.errosCompilacao = []
      
      this.mostrarSnackbar(`Template de ${template.nome} carregado com sucesso`)
    },
    
    async compilarContrato() {
      if (!this.editorCodigo.trim()) {
        this.mostrarSnackbar('O código está vazio', 'error')
        return
      }
      
      this.compilando = true
      this.errosCompilacao = []
      this.codigoCompilado = false
      
      try {
        // Compilar via API real do kernel
        const { ExplorerAPI } = await import('@/services/api')
        const response = await fetch(`${API_BASE_URL}/api/v1/compile`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            code: this.editorCodigo,
            name: this.arquivoAtual?.name?.replace('.qtz', '') || 'Contract'
          })
        })
        const resultado = await response.json()
        
        if (resultado.status === 'compiled') {
          this.codigoCompilado = true
          this.bytecodeGerado = resultado.wasm_base64
          this.mostrarSnackbar(`Compilação bem-sucedida! (${resultado.wasm_size} bytes WASM)`)
          this.adicionarMensagemAssistente(`Contrato compilado: ${resultado.wasm_size} bytes WASM. Pronto para deploy.`)
        } else {
          this.errosCompilacao = [{
            linha: 1,
            mensagem: 'Erro de compilação',
            contexto: resultado.errors || resultado.output || 'Erro desconhecido'
          }]
          this.mostrarSnackbar('Erros de compilação encontrados', 'error')
          this.adicionarMensagemAssistente(`Erro na compilação: ${resultado.errors || resultado.output}`)
        }
      } catch (error) {
        console.error('Erro ao compilar:', error)
        this.mostrarSnackbar('Erro ao conectar ao compilador', 'error')
      } finally {
        this.compilando = false
      }
    },
    
    implantarContrato() {
      if (!this.codigoCompilado) {
        this.mostrarSnackbar('Compile o contrato antes de implantá-lo', 'error')
        return
      }
      
      this.implantacao.nome = 'MeuContrato'
      this.dialogImplantacao = true
    },
    
    async confirmarImplantacao() {
      this.implantando = true
      
      try {
        // Deploy real via API do kernel
        const response = await fetch(`${API_BASE_URL}/api/v1/contracts/deploy`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            code: this.bytecodeGerado,
            format: 'wasm',
            deployer: this.implantacao.endereco || (window.__QUARNX_WALLET_ADDR__ || '0'.repeat(64))
          })
        })
        const resultado = await response.json()
        
        if (resultado.status === 'deployed') {
          const novoContrato = {
            nome: this.implantacao.nome,
            address: resultado.contract_address,
            timestampDeployment: new Date(),
            bytecode: this.bytecodeGerado
          }
          
          await this.developerStore.addContract(novoContrato)
          
          this.mostrarSnackbar(`Contrato implantado: ${resultado.contract_address.slice(0, 16)}...`)
          this.dialogImplantacao = false
          this.adicionarMensagemAssistente(`Contrato deployado no endereço ${resultado.contract_address}`)
        } else {
          this.mostrarSnackbar(`Erro: ${resultado.status}`, 'error')
        }
      } catch (error) {
        console.error('Erro ao implantar:', error)
        this.mostrarSnackbar('Erro ao implantar contrato', 'error')
      } finally {
        this.implantando = false
      }
    },
    
    enviarMensagem() {
      if (!this.novaMensagem.trim()) return
      
      // Adiciona mensagem do usuário
      this.chatMensagens.push({
        tipo: 'usuario',
        texto: this.novaMensagem
      })
      
      const pergunta = this.novaMensagem
      this.novaMensagem = ''
      
      // Simula processamento e resposta do assistente
      setTimeout(() => {
        let resposta = ''
        
        if (pergunta.toLowerCase().includes('compilar')) {
          resposta = 'Para compilar seu contrato, clique no botão "Compilar" no topo da página. Certifique-se de que seu código não contém erros de sintaxe.'
        } else if (pergunta.toLowerCase().includes('token') || pergunta.toLowerCase().includes('qrc20')) {
          resposta = 'Para criar um token QRC20, você pode usar nosso template pré-configurado. Selecione "Token QRC20" na lista de templates à direita.'
        } else if (pergunta.toLowerCase().includes('nft')) {
          resposta = 'NFTs na QuarnX seguem um padrão similar ao ERC-721. Você pode usar nosso template de NFT básico para começar seu desenvolvimento.'
        } else if (pergunta.toLowerCase().includes('implantar') || pergunta.toLowerCase().includes('deploy')) {
          resposta = 'Para implantar seu contrato, primeiro compile-o com sucesso, depois clique no botão "Implantar". Você precisará fornecer alguns parâmetros para a implantação.'
        } else if (pergunta.toLowerCase().includes('queen')) {
          resposta = 'Queen é a linguagem de contratos inteligentes da QuarnX. Ela é similar a Solidity, mas com melhorias de segurança e verificação formal. A sintaxe básica inclui declarações de contrato, variáveis, funções e verificações formais com "verify".'
        } else {
          resposta = 'Posso ajudar com dúvidas sobre a linguagem Queen, tokens QRC20, NFTs, e o processo de compilação e implantação. Você também pode perguntar sobre problemas específicos de código.'
        }
        
        this.adicionarMensagemAssistente(resposta)
      }, 1000)
    },
    
    adicionarMensagemAssistente(texto) {
      this.chatMensagens.push({
        tipo: 'assistente',
        texto: texto
      })
      
      // Rola para o final do chat
      this.$nextTick(() => {
        if (this.$refs.chatContainer) {
          this.$refs.chatContainer.scrollTop = this.$refs.chatContainer.scrollHeight
        }
      })
    },
    
    solicitarAjuda() {
      this.adicionarMensagemAssistente('Analisando seu código...')
      
      setTimeout(() => {
        this.adicionarMensagemAssistente('Detectei que você está trabalhando em um contrato Queen. Posso ajudar com orientações sobre estrutura, boas práticas ou resolver problemas específicos. O que você gostaria de saber?')
      }, 1500)
    },
    
    mostrarSnackbar(texto, cor = 'success') {
      this.snackbar.text = texto
      this.snackbar.color = cor
      this.snackbar.show = true
    },
    
    verificarMudancasNaoSalvas(event) {
      if (this.codigoModificado) {
        const message = 'Existem alterações não salvas no editor.'
        event.returnValue = message
        return message
      }
    }
  },
  mounted() {
    // Inicializa com um template básico ao iniciar o componente
    setTimeout(() => {
      if (!this.arquivoAtual) {
        const sampleFile = {
          id: 1,
          name: 'MeuContrato.queen',
          type: 'contract',
          projectId: 1
        }
        this.selecionarArquivo(sampleFile)
      }
    }, 500)
    
    // Adicionando listener para evento beforeunload do navegador
    window.addEventListener('beforeunload', this.verificarMudancasNaoSalvas)
  },
  
  beforeUnmount() {
    // Removendo listener quando o componente é desmontado
    window.removeEventListener('beforeunload', this.verificarMudancasNaoSalvas)
  }
}
</script>

<style scoped>
.ide-view {
  padding: 20px;
}

.code-editor {
  font-family: monospace;
  background-color: #1e1e1e;
  color: #d4d4d4;
  height: 100%;
}

.chat-container {
  display: flex;
  flex-direction: column;
}

.chat-message {
  max-width: 80%;
}

.explorer-height {
  height: 500px;
}

.contract-sandbox-height {
  height: 500px;
}

.h-100 {
  height: 100%;
}
</style>
